import { GAME_NAME, IS_LITE } from './edition';
export const TITLE = GAME_NAME;
export const CELL = 1.25;
export const GROUND = 0.16;
export const FIXED_DT = 1 / 60;
export const LIMITS = { charges: 256, jobs: 256, audio: 22, catchUp: 4 } as const;
export const BASE_C4_RADIUS = 5.8;
/** `auto` remains readable for older session settings; adaptation is a separate opt-in. */
export type QualityName = 'auto' | 'low' | 'medium' | 'high' | 'custom';
export type PresetName = 'low' | 'medium' | 'high';
export const PRESETS = {
  low: { bodies: 160, fragmentCells: 130, particles: 120, shadows: 0, scale: 0.75, npcs: 80 },
  medium: { bodies: 280, fragmentCells: 90, particles: 240, shadows: 1024, scale: 1, npcs: 140 },
  high: { bodies: 520, fragmentCells: 60, particles: 480, shadows: 2048, scale: 1, npcs: 220 },
} as const;
export interface Settings {
  quality: QualityName;
  scale: number;
  shadows: number;
  effects: number;
  detail: number;
  density: number;
  viewDistance: number;
  sceneryDistance: number;
  physicsRadius: number;
  adaptive: boolean;
  volume: number;
  effectsVolume: number;
  musicVolume: number;
  ambienceVolume: number;
  sirenVolume: number;
  sirenMuted: boolean;
  sensitivity: number;
  reduced: boolean;
  slow: boolean;
  c4Footprint: number;
  c4Strength: number;
  airstrikeStrength: number;
  railHeadway: number;
}
export function initialSettings(_touch = false): Settings {
  return {
    quality: IS_LITE ? 'low' : 'high',
    scale: IS_LITE ? 0.85 : 1,
    shadows: IS_LITE ? 0 : 2048,
    effects: IS_LITE ? 0 : 2,
    detail: IS_LITE ? 0 : 2,
    density: IS_LITE ? 180 : 2000,
    viewDistance: IS_LITE ? 150 : 360,
    sceneryDistance: IS_LITE ? 800 : 1800,
    physicsRadius: IS_LITE ? 90 : 180,
    adaptive: false,
    volume: 0.45,
    effectsVolume: 0.9,
    musicVolume: 0.35,
    ambienceVolume: 0.65,
    sirenVolume: 0.2,
    sirenMuted: false,
    sensitivity: 1,
    reduced: false,
    slow: false,
    c4Footprint: 10,
    c4Strength: 3,
    airstrikeStrength: 1,
    railHeadway: 90,
  };
}
export function resolveQuality(name: QualityName, _touch = false): PresetName {
  return name === 'auto' || name === 'custom' ? 'high' : name;
}
export function applyVisualPreset(s: Settings, preset: PresetName): void {
  Object.assign(s, {
    quality: preset,
    scale: IS_LITE && preset === 'low' ? 0.85 : PRESETS[preset].scale,
    shadows: PRESETS[preset].shadows,
    detail: preset === 'high' ? 2 : preset === 'medium' ? 1 : 0,
    effects: preset === 'high' ? 2 : preset === 'medium' ? 1 : 0,
    viewDistance: preset === 'high' ? 360 : preset === 'medium' ? 180 : IS_LITE ? 150 : 120,
    sceneryDistance: preset === 'high' ? 1800 : preset === 'medium' ? 1200 : 800,
  });
}

/** Adjusts cosmetic resolution only; the game samples this only when opted in. */
export class AdaptiveQuality {
  factor = 1;
  private bad = 0;
  private good = 0;
  sample(ms: number, target: number): boolean {
    this.bad = ms > (1000 / target) * 1.45 ? this.bad + 1 : Math.max(0, this.bad - 1);
    this.good = ms < (1000 / target) * 1.08 ? this.good + 1 : 0;
    if (this.bad > 180 && this.factor > 0.6) {
      this.factor = Math.max(0.6, this.factor - 0.1);
      this.bad = 0;
      this.good = 0;
      return true;
    }
    if (this.good > 1200 && this.factor < 1) {
      this.factor = Math.min(1, this.factor + 0.05);
      this.good = 0;
      return true;
    }
    return false;
  }
}
